import React, { useCallback, useContext, useMemo } from 'react';
import * as Icons from '@ant-design/icons';
import { Select, Space, message } from 'antd';
import { HeadPortal } from '@modules/layout/components/head-portal';
import { StyledHeaderButton } from '@modules/layout/styled';
import { StyledActionBar } from '@shared/styled/action-bar';
import { useBackgroundNavigate, useMassiveExport } from '@shared/hooks';
import { downloadXlsx } from '@shared/utils';
import { tableFilterQueryMaker } from '@shared/modules/next-table/utils';
import { useAuth } from '@modules/me';
import { useStatusesByModelId } from '@modules/statuses/hooks';
import { UsersTableContext } from '../context';
import { UsersService } from '../services';
import { getPersistenceUserRole, getUserRole, userQueryKeys } from '../utils';

export const UsersActionBar = () => {
  const navigate = useBackgroundNavigate();
  const { can } = useAuth();
  const { state, handleFetch, handleReset, handleSelectAll, handleResetSelection, handleChangeFilterById } = useContext(UsersTableContext);
  const selectionCount = Object.values(state.selectedRowIds).filter(Boolean).length;

  const declarationStatuses = useStatusesByModelId(2);

  const { handleExport, exportedData } = useMassiveExport(UsersService.getUsersForExport, { resetKey: state.filters });

  const roleFilterValue = useMemo(() => getUserRole(state.filters.find((f) => f.id === userQueryKeys.role)?.value), [state.filters]);
  const declarationStateValue = useMemo(() => state.filters.find((f) => f.id === userQueryKeys.declarations.state.id)?.value ?? undefined, [state.filters]);
  const mobileAppValue = useMemo(() => state.filters.find((f) => f.id === userQueryKeys.mobileAppUser)?.value ?? undefined, [state.filters]);

  const handleMassiveExport = useCallback(() => {
    handleExport(tableFilterQueryMaker(state.filters));
  }, [handleExport, state.filters]);

  const exportAsExcel = useCallback(async () => {
    message.loading({ key: 'users-export', content: 'Sənəd hazırlanır...', duration: 0 });
    const result = await UsersService.exportExcel(tableFilterQueryMaker(state.filters));
    if (result.status === 200) {
      message.success({ key: 'users-export', content: 'Sənəd yüklənir.' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(result.data as Blob);
      a.download = `users_export_${Math.round(Math.random() * 1000)}.xls`;
      a.click();
    } else {
      message.error({ key: 'users-export', content: result.data as string });
    }
  }, [state.filters]);

  return (
    <>
      <HeadPortal>
        <StyledActionBar $flex={true}>
          <Space size={0}>
            <StyledHeaderButton type="text" onClick={() => navigate('/users/create', { withBackground: true })} icon={<Icons.PlusCircleOutlined />}>
              Yeni
            </StyledHeaderButton>
            {!selectionCount ? (
              <StyledHeaderButton type="text" onClick={handleSelectAll} icon={<Icons.CheckCircleOutlined />}>
                Hamısını seç
              </StyledHeaderButton>
            ) : (
              <StyledActionBar.Selection>
                <Icons.CloseCircleTwoTone twoToneColor="#ff4d4f" onClick={handleResetSelection} style={{ cursor: 'pointer', fontSize: 16 }} role="icon" />
                <span>{selectionCount} seçilib</span>
              </StyledActionBar.Selection>
            )}
            <StyledHeaderButton type="text" onClick={handleFetch} icon={<Icons.ReloadOutlined />}>
              Yenilə
            </StyledHeaderButton>
            <StyledHeaderButton type="text" onClick={handleReset} icon={<Icons.ClearOutlined />}>
              Sıfırla
            </StyledHeaderButton>
            {can('user_edit') &&
              (exportedData.length ? (
                <StyledHeaderButton type="text" onClick={() => downloadXlsx(exportedData, `users_list_${Math.round(Math.random() * 1000)}`)} icon={<Icons.FileExcelOutlined />}>
                  Yüklə
                </StyledHeaderButton>
              ) : (
                <StyledHeaderButton type="text" onClick={handleMassiveExport} icon={<Icons.FileExcelOutlined />}>
                  Export
                </StyledHeaderButton>
              ))}
          </Space>

          {!selectionCount && (
            <Space size={0}>
              <StyledHeaderButton type="text" onClick={() => navigate('/statistics/qizil-onluq', { withBackground: false })}>
                Qızıl onluq
              </StyledHeaderButton>
              {can('user_edit') && (
                <StyledHeaderButton type="text" onClick={exportAsExcel} icon={<Icons.FileExcelOutlined />}>
                  Export
                </StyledHeaderButton>
              )}
              <StyledHeaderButton type="text" onClick={() => navigate('/statistics/users/general', { withBackground: true })} icon={<Icons.BarChartOutlined />} />
              <StyledHeaderButton type="text" onClick={() => navigate('/users/discounts', { withBackground: false })}>
                Endirimli müştərilər
              </StyledHeaderButton>
            </Space>
          )}
        </StyledActionBar>
      </HeadPortal>
      <Space size={8}>
        <Select
          style={{ width: 140 }}
          value={roleFilterValue}
          placeholder="İstifadəçi tipi"
          onChange={(val) => handleChangeFilterById(userQueryKeys.role, getPersistenceUserRole(val))}
          allowClear
          showSearch
        >
          {can('admins') && <Select.Option value="admin">Adminlər</Select.Option>}
          {can('admins') && <Select.Option value="warehouseman">Anbardarlar</Select.Option>}
          <Select.Option value="back-office">Back office</Select.Option>
        </Select>
        <Select
          style={{ minWidth: 140 }}
          mode="multiple"
          placeholder="Bağlama statusu"
          value={declarationStateValue}
          showSearch
          allowClear
          loading={declarationStatuses.isLoading}
          onChange={(val) => handleChangeFilterById(userQueryKeys.declarations.state.id, val?.length ? val : undefined)}
        >
          {declarationStatuses.data?.map((s) => (
            <Select.Option key={s.id} value={s.id.toString()}>
              {s.name}
            </Select.Option>
          ))}
        </Select>
        <Select style={{ width: 160 }} value={mobileAppValue} placeholder="Mobil tətbiq" allowClear onChange={(val) => handleChangeFilterById(userQueryKeys.mobileAppUser, val ?? undefined)}>
          <Select.Option value="1">Mobil istifadəçilər</Select.Option>
          <Select.Option value="0">Mobil olmayan</Select.Option>
        </Select>
      </Space>
    </>
  );
};
